import { roundQuantity } from '../schedule/unit-of-measure';
import {
  retainingColumnFaces,
  retainingColumnVolume,
  retainingWallFace,
  retainingWallLength,
  retainingWallVolume,
  type RetainingColumn,
  type RetainingWall,
} from './retaining';

/**
 * O que a conta tira do muro e do pilar de contenção desenhados (Fase 27, etapa 4 — D9, F2, F6,
 * F7, RN-360, RN-367, RN-372).
 *
 * É a irmã de `foundation-totals.ts` e `slab-totals.ts`: as contas de **cada peça** moram em
 * `retaining.ts`, o contrato, e esta é a soma delas na tipologia — uma conta por peça (`wall` e
 * `column`), porque os serviços de um e de outro nunca se misturam. Nenhuma medida de AMBIENTE,
 * PAREDE, PILAR, VIGA ou das peças anteriores soma o que está aqui (RN-360), e o muro e o pilar
 * de contenção não somam nenhuma delas (F2).
 *
 * > **A regra em uma linha (RN-372):** o pilar encaixado tira do muro a largura dele na altura em
 * > comum, e o muro tira do pilar as faces que ficam dentro dele — e os dois lados da conta são
 * > os da **mesma tipologia**.
 */

/** O concreto mora na terceira casa, como o do bloco: duas comeriam o grama do aço dele. */
export const RETAINING_VOLUME_DECIMALS = 3;

/** Faces, comprimento e altura: duas casas, como a área e o perímetro do ambiente. */
export const RETAINING_AREA_DECIMALS = 2;

export interface RetainingWallTotals {
  /** Quantos muros (un). */
  readonly count: number;
  /** O comprimento do eixo de cada muro, somado (m). */
  readonly length: number;
  /** Comprimento × altura de cada muro, sem os pilares encaixados, somado (m²). */
  readonly face: number;
  /** A face (sem os pilares) × espessura, somada (m³). */
  readonly volume: number;
}

export interface RetainingColumnTotals {
  /** Quantos pilares de contenção (un). */
  readonly count: number;
  /** A altura de cada pilar, somada (m) — o LENGTH. */
  readonly height: number;
  /** As faces com fôrma, sem as que ficam dentro do muro, somadas (m²). */
  readonly faces: number;
  /** Seção × altura de cada pilar, somado (m³). */
  readonly volume: number;
}

/** Sem muro desenhado — o ponto de partida, e o que a tipologia sem contenção devolve. */
export const NO_RETAINING_WALLS: RetainingWallTotals = { count: 0, length: 0, face: 0, volume: 0 };

/** Sem pilar de contenção desenhado. */
export const NO_RETAINING_COLUMNS: RetainingColumnTotals = {
  count: 0,
  height: 0,
  faces: 0,
  volume: 0,
};

function soma(valores: readonly number[], casas: number): number {
  return roundQuantity(
    valores.reduce((total, valor) => total + valor, 0),
    casas,
  );
}

/**
 * A conta dos muros da tipologia. Cada muro desconta os pilares **da mesma tipologia** que estão
 * encaixados nele (`retainingWallFace`, `retainingWallVolume`); o excluído não conta nem
 * desconta.
 */
export function retainingWallTotals(
  walls: readonly RetainingWall[],
  columns: readonly RetainingColumn[] = [],
): RetainingWallTotals {
  const vivos = walls.filter((wall) => wall.deletedAt === undefined);
  return {
    count: vivos.length,
    length: soma(vivos.map(retainingWallLength), RETAINING_AREA_DECIMALS),
    face: soma(
      vivos.map((wall) => retainingWallFace(wall, columns)),
      RETAINING_AREA_DECIMALS,
    ),
    volume: soma(
      vivos.map((wall) => retainingWallVolume(wall, columns)),
      RETAINING_VOLUME_DECIMALS,
    ),
  };
}

/**
 * A conta dos pilares de contenção da tipologia. Cada pilar perde as faces que ficam dentro do
 * muro **da mesma tipologia** em que está encaixado (`retainingColumnFaces`); o muro excluído não
 * encaixa pilar nenhum.
 */
export function retainingColumnTotals(
  columns: readonly RetainingColumn[],
  walls: readonly RetainingWall[] = [],
): RetainingColumnTotals {
  const vivos = columns.filter((column) => column.deletedAt === undefined);
  return {
    count: vivos.length,
    height: soma(
      vivos.map((column) => column.height),
      RETAINING_AREA_DECIMALS,
    ),
    faces: soma(
      vivos.map((column) => retainingColumnFaces(column, walls)),
      RETAINING_AREA_DECIMALS,
    ),
    volume: soma(vivos.map(retainingColumnVolume), RETAINING_VOLUME_DECIMALS),
  };
}

/**
 * Os totais do muro multiplicados pelos andares que repetem o desenho (RN-167), como o bloco e a
 * laje. Cada número volta à casa dele; a contagem é inteira.
 */
export function scaleRetainingWallTotals(
  totals: RetainingWallTotals,
  times: number,
): RetainingWallTotals {
  if (times === 1) return totals;
  const area = (valor: number): number => roundQuantity(valor * times, RETAINING_AREA_DECIMALS);
  return {
    count: totals.count * times,
    length: area(totals.length),
    face: area(totals.face),
    volume: roundQuantity(totals.volume * times, RETAINING_VOLUME_DECIMALS),
  };
}

/** Os totais do pilar de contenção multiplicados pelos andares que repetem o desenho. */
export function scaleRetainingColumnTotals(
  totals: RetainingColumnTotals,
  times: number,
): RetainingColumnTotals {
  if (times === 1) return totals;
  const area = (valor: number): number => roundQuantity(valor * times, RETAINING_AREA_DECIMALS);
  return {
    count: totals.count * times,
    height: area(totals.height),
    faces: area(totals.faces),
    volume: roundQuantity(totals.volume * times, RETAINING_VOLUME_DECIMALS),
  };
}
